var _react_refresh_temp_1;
_react_refresh_temp_1 = $RefreshSig$();
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';
import { NotFoundError } from '@lucidlink/sdk/fetcher';
import { getRootLogger } from '@lucidlink/tscore';
import { useAuthProvider, useDependency, useLucidSdk, useSubscribeToBroadcastMessage, } from '#context';
import { useSsoLogin } from '#features/auth';
import { getWorkspaceLoginConfigByNameQueryFactory, getWorkspaceSsoLoginParamsQueryFactory, useWorkspaceSsoLogin, } from '#query-hooks';
import { WorkspaceNameNotFoundError, WorkspaceSsoNotConfiguredError, } from '../../../../errors';
export const useAddWorkspaceSsoLogin = ({ onError: onErrorOuter, onSuccess: onSuccessOuter, }) => {
    _react_refresh_temp_1();
    const { lucidSdk } = useLucidSdk();
    const { currentUser } = useAuthProvider();
    const queryClient = useQueryClient();
    // Platform-specific SSO initialization (injected via DI)
    const useStartSsoAuthentication = useDependency('useStartSsoAuthentication');
    const { exchangeCode, startSsoAuthentication } = useStartSsoAuthentication();
    // Store promise-like resolve/reject for auth code handling
    // Promise-like resolve/reject are set after starting SSO authentication
    // This is done so we can await the auth code in the onAuthCodeReceived callback, which is triggered by a broadcast message from the SSO landing page
    // otherwise we cannot really track the 'loading' state of the whole operation, we would need some intermediate state to track the pending
    // auth code exchange after starting SSO authentication
    const authCodeMessagePromiseLikeRef = useRef(null);
    const { mutateAsync: ssoLoginPromise } = useSsoLogin();
    const { mutateAsync: workspaceSsoLoginPromise } = useWorkspaceSsoLogin();
    const startSsoLoginMutation = useMutation({
        mutationFn: async (workspaceName) => {
            const workspaceLoginConfig = await queryClient
                .fetchQuery({
                ...getWorkspaceLoginConfigByNameQueryFactory(lucidSdk, workspaceName),
                retry: false,
            })
                // eslint-disable-next-line no-restricted-syntax -- This syntax is more readable imo and takes less space
                .catch((error) => {
                remapWorkspaceLoginConfigErrors(error, workspaceName);
            });
            const workspaceSsoLoginParams = await queryClient
                .fetchQuery({
                ...getWorkspaceSsoLoginParamsQueryFactory(lucidSdk, workspaceLoginConfig.id),
                retry: false,
            })
                // eslint-disable-next-line no-restricted-syntax -- This syntax is more readable imo and takes less space
                .catch((error) => {
                remapWorkspaceSsoLoginParamsErrors(error, workspaceName);
            });
            await startSsoAuthentication(workspaceSsoLoginParams, currentUser?.email ?? '');
            const authCode = await new Promise((resolve, reject) => {
                authCodeMessagePromiseLikeRef.current = { resolve, reject };
            });
            const tokenEndpointResponse = await exchangeCode(authCode);
            if (!tokenEndpointResponse?.access_token) {
                throw new Error('No access token received');
            }
            await ssoLoginPromise({
                accessToken: tokenEndpointResponse.access_token,
                skipGlobalLogin: true,
                loginEmail: currentUser?.email ?? '',
            });
            await workspaceSsoLoginPromise({
                workspaceId: workspaceLoginConfig.id,
                accessToken: tokenEndpointResponse.access_token,
            });
            return {
                workspaceId: workspaceLoginConfig.id,
            };
        },
        onSuccess: (data) => {
            getRootLogger().info({ email: currentUser?.email ?? '' }, 'Add workspace sso login succeeded');
            if (typeof onSuccessOuter === 'function') {
                onSuccessOuter(data);
            }
        },
        onError: (error) => {
            if (typeof onErrorOuter === 'function') {
                onErrorOuter(error);
            }
            getRootLogger().error({ err: error, email: currentUser?.email ?? '' }, 'Add workspace sso login failed');
        },
        // Supposedly, onSuccess/onError fire BEFORE onSettled, so the refs should still be populated when onSuccess/onError are called
        onSettled: () => {
            // Clear auth code ref so we do not pollute future login attempts
            authCodeMessagePromiseLikeRef.current = null;
        },
    });
    const onAuthCodeReceived = useCallback((message) => {
        if (message.type !== 'auth-code-received') {
            return;
        }
        authCodeMessagePromiseLikeRef.current?.resolve(message.payload);
    }, []);
    useSubscribeToBroadcastMessage({
        callback: onAuthCodeReceived,
        messageType: 'auth-code-received',
    });
    return useMemo(() => {
        return {
            startSsoLoginMutation,
        };
    }, [startSsoLoginMutation]);
};
_react_refresh_temp_1(useAddWorkspaceSsoLogin, "68gMeYNFmMJxyrXMA1EYOrWLnJY=", true, () => [useLucidSdk, useAuthProvider, useQueryClient, useDependency]);
function remapWorkspaceLoginConfigErrors(error, workspaceName) {
    if (error instanceof NotFoundError) {
        throw new WorkspaceNameNotFoundError(`Workspace with name "${workspaceName}" not found.`);
    }
    throw error;
}
function remapWorkspaceSsoLoginParamsErrors(error, workspaceName) {
    if (error instanceof NotFoundError) {
        throw new WorkspaceSsoNotConfiguredError(`Workspace with name "${workspaceName}" does not have SSO configured.`);
    }
    throw error;
}
